import { AfterViewInit, Component, EventEmitter, OnInit } from '@angular/core';
import { ISavedDashboardViewComponentOptions } from 'src/app/components/dashboard/saved-dashboard-view/saved-dashboard-view.component';
import { IMultipleSelectDashboardItem } from 'src/app/components/multiple-select/multiple-select.component';
import { CommonLegendService } from 'src/app/services/common-legend.service';
import { IDashboardItemDataLoaderLink } from 'src/app/services/dashboard-item/IDashboardItemDataLoaderLink';
import {
    DashboardService,
    IDashboardInitOptions,
} from 'src/app/services/dashboard/dashboard.service';
import { DataLoaderFactories } from 'src/app/services/data-loader/data-loader.service';
import {
    IQueryContext,
    QueryContextDefaultSettings,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';
import { QueryContextService } from 'src/app/services/query-context/query-context.service';
import { DatasourceColumnId } from 'src/app/utils/datasource-sql-map/datasource';
import { EChartsTooltipHelper } from 'src/app/utils/echarts/echarts-tooltip-helper';
import { defaultColorGetterBuilder } from 'src/app/utils/echarts/series-color';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { currentDataProject } from 'src/app/services/data-project/models/data-project.model';
import { DashboardItemFactories } from '../common/items/common-dashboard-items';
import { ModifyTimeDashboardItemFactoryOptions } from '../common/items/modify-time';
import { StandardDashboardWorker } from '../common/standard-dashboard';
import { MopaFunnelsChartDocs } from './mopa-funnels.chart-documentation';
import { FunnelsMasterFunnelDataLoaderFactory } from './data-loaders/FunnelsMasterFunnel';
import { BiDevMasterFunnelSqlMap } from './sql-maps/bidev-master-funnel-sql-map';
import { IDataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { EChartsAxisHelper } from 'src/app/utils/echarts/echarts-axis-helper';
import { FunnelsMasterFunnelPlayerEventsDataLoaderFactory } from './data-loaders/FunnelsMasterFunnelPlayerEvents';
import { BiDevPlayerEventsExtMasterFunnelSqlMap } from './sql-maps/bidev-player-events-ext-master-funnel-agg-cleaned';
import { BiDevMasterFunnelStepsSqlMap } from './sql-maps/bidev-master-funnel-steps';
import { IStringInputQueryContextSettingDashboardItem } from '../common/items/setting/string-input-setting/string-input-setting.component';
import { ISingleSelectSettingDashboardItem } from '../common/items/setting/single-select/single-select.component';
import { CategoryLabelSortFn } from 'src/app/utils/echarts/xy-chart-axis';

@Component({
    selector: 'app-mopa-funnels',
    templateUrl: './mopa-funnels.component.html',
    styleUrls: ['./mopa-funnels.component.scss'],
    providers: [DashboardService, QueryContextService, CommonLegendService],
})
export class MopaFunnelsComponent
    extends StandardDashboardWorker
    implements OnInit, AfterViewInit
{
    override DASHBOARD_NAME = 'Funnels';
    override DASHBOARD_ID = 'mopa-funnels';
    override dashboardInitOptions: IDashboardInitOptions = {};
    override dashboardDocumentation: string =
        MopaFunnelsChartDocs.dashboard;

    private defaultGcpProjectId: GcpProjectId =
        currentDataProject().gcpProjectId;
    public savedDashboardViewComponentOptions: ISavedDashboardViewComponentOptions =
        {};

    ////-------------------------------------------------------------
    ////
    ////    WYKRESY
    ////
    /////////////////////////////////////////////////////////////////

    /////////////////////////////////////////////////////////////////
    ////
    ////    DATALOADERY WYKRESÓW
    ////    Dataloader dostarcza danych
    ////    Dataloadery wykresów są tworzone w tym miejscu
    ////    Kilka wykresów może korzystać z tego samego dataloadera
    ////
    /////////////////////////////////////////////////////////////////

    override chartDataLoaders = {
        'mopa.funnels.modify_time_datasource': DataLoaderFactories[
            'common.modify_time'
        ]({
            createdInstanceId: 'mopa.funnels.modify_time_users_datasource',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            datasetId: BiDevMasterFunnelSqlMap.sqlMap.dataset,
            tableId: BiDevMasterFunnelSqlMap.sqlMap.tableId,
        }).create(),
        'mopa.funnels.master_funnel': new FunnelsMasterFunnelDataLoaderFactory({
            createdInstanceId: 'mopa.funnels.master_funnel',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
        }).create(),
        'mopa.funnels.master_funnel_details':
            new FunnelsMasterFunnelPlayerEventsDataLoaderFactory({
                createdInstanceId: 'mopa.funnels.master_funnel_details',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
            }).create(),
    };

    /////////////////////////////////////////////////////////////////
    ////
    ////    CONFIGI WYKRESÓW
    ////    Konfiguracje wykresów są tworzone w tym miejscu
    ////    Każdy wykres ma swoją konfigurację
    ////    Tutaj wskazujemy dataloader, który ma dostarczać dane
    ////
    /////////////////////////////////////////////////////////////////

    modify_time_master_funnel: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.funnels.modify_time_master_funnel',
        dashboardItemFactory: DashboardItemFactories['common.modify_time'](
            new ModifyTimeDashboardItemFactoryOptions(
                'mopa.funnels.modify_time_master_funnel',
                'value'
            )
        ),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.funnels.modify_time_datasource']
                .emitter,
    };

    masterStepSortFn: CategoryLabelSortFn = (a: string, b: string) => {
        // split by ':' and compare as numbers
        const aNum = parseInt(a.split(':')[0], 10);
        const bNum = parseInt(b.split(':')[0], 10);
        return aNum - bNum;
    };

    detailsEventsSortFn: CategoryLabelSortFn = (a: string, b: string) => {
        // split by '.' and compare as numbers
        const aNum = parseInt(a.split('.')[0], 10);
        const bNum = parseInt(b.split('.')[0], 10);
        return aNum - bNum;
    };

    master_funnel_cnt: IDashboardItemDataLoaderLink = {
        id: 'mopa.funnels.chart.master_funnel_cnt',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.funnels.chart.master_funnel_cnt',
            dashboardItem: {
                title: 'Master funnel',
                chartDocumentation: MopaFunnelsChartDocs.master_funnel_cnt,
            },
            xAxisType: 'category',
            xAxisCategorySubtype: 'generic',
            xColumnLabel: 'master_funnel_step_id',
            xAxisSortFn: this.masterStepSortFn,
            yColumnLabel: 'cnt',
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            metricName: 'cnt',
            echartsSeriesType: 'bar',
            tooltip: {
                // xLabelFormatterGetter: () => (value: string) =>
                //     `Step_id: ${value}`,
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipIntegerValueFormatter,
            },
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.funnels.master_funnel'].emitter,
    };

    master_funnel_step_diff: IDashboardItemDataLoaderLink = {
        id: 'mopa.funnels.chart.master_funnel_step_diff',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.funnels.chart.master_funnel_step_diff',
            dashboardItem: {
                title: 'Master funnel % change',
                chartDocumentation:
                    MopaFunnelsChartDocs.master_funnel_step_diff,
            },
            xAxisType: 'category',
            xAxisCategorySubtype: 'generic',
            xAxisSortFn: this.masterStepSortFn,
            yAxisLabel: {
                formatter: (v: number) =>
                    EChartsAxisHelper.axisPercentLabelFormatter(v, 0),
            },
            xColumnLabel: 'master_funnel_step_id',
            yColumnLabel: 'pct_diff',
            metricName: 'pct_diff',
            echartsSeriesType: 'bar',
            tooltip: {
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipPercentValueFormatter,
            },
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.funnels.master_funnel'].emitter,
    };

    master_funnel_step_diff_first: IDashboardItemDataLoaderLink = {
        id: 'mopa.funnels.chart.master_funnel_step_diff_first',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.funnels.chart.master_funnel_step_diff_first',
            dashboardItem: {
                title: 'Master funnel % change',
                chartDocumentation:
                    MopaFunnelsChartDocs.master_funnel_step_diff_first,
            },
            xAxisType: 'category',
            xAxisCategorySubtype: 'generic',
            xAxisSortFn: this.masterStepSortFn,
            yAxisLabel: {
                formatter: (v: number) =>
                    EChartsAxisHelper.axisPercentLabelFormatter(v, 0),
            },
            xColumnLabel: 'master_funnel_step_id',
            yColumnLabel: 'pct_diff_first',
            metricName: 'pct_diff_first',
            echartsSeriesType: 'bar',
            tooltip: {
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipPercentValueFormatter,
            },
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.funnels.master_funnel'].emitter,
    };

    master_funnel_details_cnt: IDashboardItemDataLoaderLink = {
        id: 'mopa.funnels.chart.master_funnel_details_cnt',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.funnels.chart.master_funnel_details_cnt',
            dashboardItem: {
                title: 'Details events count',
                chartDocumentation:
                    MopaFunnelsChartDocs.master_funnel_details_cnt,
            },
            xAxisType: 'category',
            xAxisCategorySubtype: 'generic',
            xColumnLabel: 'step_name',
            xAxisSortFn: this.detailsEventsSortFn,
            yColumnLabel: (context: IQueryContext) => {
                const cntStrategy =
                    context.settings['funnels.stepCountStrategy'] ??
                    'master_step_cnt';
                return cntStrategy;
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            metricName: 'cnt',
            echartsSeriesType: 'bar',
            tooltip: {
                // xLabelFormatterGetter: () => (value: string) =>
                //     `Step_id: ${value}`,
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipIntegerValueFormatter,
            },
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.funnels.master_funnel_details'].emitter,
    };

    master_funnel_details_cnt_step_diff: IDashboardItemDataLoaderLink = {
        id: 'mopa.funnels.chart.master_funnel_details_cnt_step_diff',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.funnels.chart.master_funnel_details_cnt_step_diff',
            dashboardItem: {
                title: 'Details % change',
                chartDocumentation:
                    MopaFunnelsChartDocs.master_funnel_details_cnt_step_diff,
            },
            xAxisType: 'category',
            xAxisCategorySubtype: 'generic',
            yAxisLabel: {
                formatter: (v: number) =>
                    EChartsAxisHelper.axisPercentLabelFormatter(v, 0),
            },
            xColumnLabel: 'step_name',
            xAxisSortFn: this.detailsEventsSortFn,
            yColumnLabel: 'pct_diff',
            metricName: 'pct_diff',
            echartsSeriesType: 'bar',
            tooltip: {
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipPercentValueFormatter,
            },
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.funnels.master_funnel_details'].emitter,
    };

    master_funnel_details_cnt_step_diff_first: IDashboardItemDataLoaderLink = {
        id: 'mopa.funnels.chart.master_funnel_details_cnt_step_diff_first',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.funnels.chart.master_funnel_details_cnt_step_diff_first',
            dashboardItem: {
                title: 'Details % change',
                chartDocumentation:
                    MopaFunnelsChartDocs.master_funnel_details_cnt_step_diff_first,
            },
            xAxisType: 'category',
            xAxisCategorySubtype: 'generic',
            yAxisLabel: {
                formatter: (v: number) =>
                    EChartsAxisHelper.axisPercentLabelFormatter(v, 0),
            },
            xColumnLabel: 'step_name',
            xAxisSortFn: this.detailsEventsSortFn,
            yColumnLabel: 'pct_diff_first',
            metricName: 'pct_diff_first',
            echartsSeriesType: 'bar',
            tooltip: {
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipPercentValueFormatter,
            },
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.funnels.master_funnel_details'].emitter,
    };

    // table_summary = {
    //     id: 'lnk.mopa.funnels.cohorts_overview_table',
    //     dashboardItem: TableDashboardItemHelper.getTableDashboardItem({
    //         dashboardName: this.DASHBOARD_NAME,
    //         instanceId: 'mopa.devices.cohorts_overview_table',
    //         dashboardItem: {
    //             title: 'Cohort overview',
    //         },
    //         columnsGetter:
    //             MopaFunnelsComponent.cohortOverviewTableColumnsGetter,
    //         nzPageSize: 10,
    //         // nzTableLayout: 'auto',
    //     }),
    //     dataLoaderEmitter:
    //         this.chartDataLoaders['mopa.funnels.cohorts_overview_table']
    //             .emitter,
    // };

    ////-------------------------------------------------------------
    ////
    ////    SETTINGSY
    ////
    /////////////////////////////////////////////////////////////////

    settingLimitDetailsSteps: IStringInputQueryContextSettingDashboardItem = {
        id: QueryContextSettingID.FUNNELS_LIMIT_DETAIL_STEPS,
        viewtype: 'string-input',
        displayName: 'Limit details steps',
        value: this.getInitialContext().settings[
            QueryContextSettingID.FUNNELS_LIMIT_DETAIL_STEPS
        ],
    };

    settingStepCountStrategy: ISingleSelectSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.FUNNELS_STEP_COUNT_STRATEGY,
            viewtype: 'singleSelect',
            displayName: 'Details events count strategy',
            value: this.getInitialContext().settings[
                QueryContextSettingID.FUNNELS_STEP_COUNT_STRATEGY
            ],
            selectOptions: [
                {
                    value: 'master_step_cnt',
                    label: 'Local - zlicza wystąpienia eventu tylko w wybranych master stepach ',
                },
                {
                    value: 'total_cnt',
                    label: 'Total - zlicza wystąpienia eventu w całej przygodzie',
                },
            ],
        },
    };

    settingsOtherGroupingStrategy: ISingleSelectSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.FUNNELS_OTHER_GROUPING_STRATEGY,
            viewtype: 'singleSelect',
            displayName: "Details group outliers into 'Other' step:",
            value: this.getInitialContext().settings[
                QueryContextSettingID.FUNNELS_OTHER_GROUPING_STRATEGY
            ],
            selectOptions: [
                { value: 'raw', label: 'No grouping' },
                {
                    value: 'dynamic',
                    label: 'Dynamic - jeżeli selected_steps_cnt eventu / first_selected_steps_cnt < 0.03',
                },
                {
                    value: 'master_step',
                    label: 'Master step context - jeżeli master_step_cnt eventu / first_master_step_cnt < 0.03',
                },
                {
                    value: 'adventure',
                    label: 'Adventure context - jeżeli total_cnt eventu / first_master_step_cnt < 0.03',
                },
            ],
        },
    };

    /////////////////////////////////////////////////////////////////
    ////
    ////    SETTINGSY
    ////
    ////-------------------------------------------------------------

    ////-------------------------------------------------------------
    ////
    ////    FILTRY
    ////
    /////////////////////////////////////////////////////////////////

    ////    DATALOADERY FILTRÓW
    override filterDataLoaders = {
        'mopa.funnels.filter.project_id': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.funnels.filter.project_id',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'PROJECT_ID',
            datasourceSqlMap: BiDevMasterFunnelSqlMap.sqlMap,
        }).create(),
        // master_funnel_step_id
        'mopa.funnels.filter.master_funnel_step_id': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.funnels.filter.master_funnel_step_id',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FUNNELS_MASTER_FUNNEL_STEP_ID',
            datasourceSqlMap: BiDevMasterFunnelStepsSqlMap.sqlMap,
            filterContext: true,
            orderByQuery:
                'CAST(SPLIT(master_funnel_step_id, ":")[OFFSET(0)] AS INT64)',
        }).create(),
        'mopa.funnels.filter.details_event_name': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.funnels.filter.funnel_event',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FUNNELS_DETAILS_EVENT_NAME',
            datasourceSqlMap: BiDevPlayerEventsExtMasterFunnelSqlMap.sqlMap,
            filterContext: true,
        }).create(),
        'mopa.funnels.filter.details_screen_name': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.funnels.filter.screen_name',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FUNNELS_DETAILS_SCREEN_NAME',
            datasourceSqlMap: BiDevPlayerEventsExtMasterFunnelSqlMap.sqlMap,
            filterContext: true,
            additionalSQLTokens: {
                where: [
                    'is_master_funnel_step_scope_other = FALSE',
                    "master_funnel_step_id != '_NULL_'",
                ],
            },
        }).create(),

        // 'mopa.funnels.filter.inapp_source': DataLoaderFactories[
        //     'common.filter.datasource_column'
        // ]({
        //     createdInstanceId: 'mopa.funnels.filter.inapp_source',
        //     gcpProjectId: this.defaultGcpProjectId,
        //     queryCacheService: this.queryCache,
        //     datasourceColumnId: 'SALES_INAPP_SOURCE',
        //     datasourceSqlMap: BIVizDashboardSales.sqlMap,
        // }).create(),
        // 'mopa.funnels.filter.inapp_product_id': DataLoaderFactories[
        //     'common.filter.datasource_column'
        // ]({
        //     createdInstanceId: 'mopa.funnels.filter.inapp_product_id',
        //     gcpProjectId: this.defaultGcpProjectId,
        //     queryCacheService: this.queryCache,
        //     datasourceColumnId: 'SALES_INAPP_PRODUCT_ID',
        //     datasourceSqlMap: BIVizDashboardSales.sqlMap,
        // }).create(),
        // 'mopa.funnels.filter.activity_app_version': DataLoaderFactories[
        //     'common.filter.datasource_column'
        // ]({
        //     createdInstanceId: 'mopa.funnels.filter.app_version',
        //     gcpProjectId: this.defaultGcpProjectId,
        //     queryCacheService: this.queryCache,
        //     datasourceColumnId: 'ACTIVITY_APP_VERSION',
        //     datasourceSqlMap: BIVizDashboardSales.sqlMap,
        //     orderByQuery: datasourceColumnOrderByQuery('ACTIVITY_APP_VERSION'),
        // }).create(),
        // 'mopa.funnels.filter.segment': DataLoaderFactories[
        //     'common.filter.segment'
        // ](
        //     new SegmentDatasourceColumnDataLoaderFactoryOptions(
        //         'mopa.funnels.filter_segments',
        //         this.defaultGcpProjectId,
        //         this.queryCache
        //     )
        // ).create(),
        // 'mopa.funnels.filter.firebase_app_store': DataLoaderFactories[
        //     'common.filter.datasource_column'
        // ]({
        //     createdInstanceId: 'mopa.funnels.filter.firebase_app_store',
        //     gcpProjectId: this.defaultGcpProjectId,
        //     queryCacheService: this.queryCache,
        //     datasourceColumnId: 'FIREBASE_APP_STORE',
        //     datasourceSqlMap: BIVizDashboardSales.sqlMap,
        // }).create(),
        // 'mopa.funnels.filter.firebase_country_european_union':
        //     DataLoaderFactories['common.filter.datasource_column']({
        //         createdInstanceId:
        //             'mopa.funnels.filter.firebase_country_european_union',
        //         gcpProjectId: this.defaultGcpProjectId,
        //         queryCacheService: this.queryCache,
        //         datasourceColumnId: 'FIREBASE_COUNTRY_EUROPEAN_UNION',
        //         datasourceSqlMap: BIVizDashboardSales.sqlMap,
        //     }).create(),
        // 'mopa.funnels.filter.firebase_country_tier': DataLoaderFactories[
        //     'common.filter.datasource_column'
        // ]({
        //     createdInstanceId: 'mopa.funnels.filter.firebase_country_tier',
        //     gcpProjectId: this.defaultGcpProjectId,
        //     queryCacheService: this.queryCache,
        //     datasourceColumnId: 'FIREBASE_COUNTRY_TIER',
        //     datasourceSqlMap: BIVizDashboardSales.sqlMap,
        //     // filterContext: ['FIREBASE_COUNTRY'],
        // }).create(),
        // 'mopa.funnels.filter.firebase_country': DataLoaderFactories[
        //     'common.filter.datasource_column'
        // ]({
        //     createdInstanceId: 'mopa.funnels.filter.firebase_country',
        //     gcpProjectId: this.defaultGcpProjectId,
        //     queryCacheService: this.queryCache,
        //     datasourceColumnId: 'FIREBASE_COUNTRY',
        //     datasourceSqlMap: BIVizDashboardSales.sqlMap,
        //     filterContext: ['FIREBASE_COUNTRY_TIER'],
        // }).create(),
        // 'mopa.funnels.filter.attribution_traffic_source': DataLoaderFactories[
        //     'common.filter.datasource_column'
        // ]({
        //     createdInstanceId: 'mopa.funnels.filter.attribution_traffic_source',
        //     gcpProjectId: this.defaultGcpProjectId,
        //     queryCacheService: this.queryCache,
        //     datasourceColumnId: 'ATTRIBUTION_TRAFFIC_SOURCE',
        //     datasourceSqlMap: BIVizDashboardSales.sqlMap,
        // }).create(),
        // 'mopa.funnels.filter.firebase_install_version': DataLoaderFactories[
        //     'common.filter.datasource_column'
        // ]({
        //     createdInstanceId: 'mopa.funnels.filter.firebase_install_version',
        //     gcpProjectId: this.defaultGcpProjectId,
        //     queryCacheService: this.queryCache,
        //     datasourceColumnId: 'FIREBASE_INSTALL_VERSION',
        //     datasourceSqlMap: BIVizDashboardSales.sqlMap,
        //     filterContext: true,
        //     orderByQuery: datasourceColumnOrderByQuery(
        //         'FIREBASE_INSTALL_VERSION'
        //     ),
        // }).create(),
        // 'mopa.funnels.filter.etl_payer': DataLoaderFactories[
        //     'common.filter.datasource_column'
        // ]({
        //     createdInstanceId: 'mopa.funnels.filter.etl_payer',
        //     gcpProjectId: this.defaultGcpProjectId,
        //     queryCacheService: this.queryCache,
        //     datasourceColumnId: 'ETL_PAYER',
        //     datasourceSqlMap: BIVizDashboardSales.sqlMap,
        // }).create(),
        // 'mopa.funnels.filter.etl_revenue_badge': DataLoaderFactories[
        //     'common.filter.datasource_column'
        // ]({
        //     createdInstanceId: 'mopa.funnels.filter.etl_revenue_badge',
        //     gcpProjectId: this.defaultGcpProjectId,
        //     queryCacheService: this.queryCache,
        //     datasourceColumnId: 'ETL_REVENUE_BADGE',
        //     datasourceSqlMap: BIVizDashboardSales.sqlMap,
        //     orderByQuery: datasourceColumnOrderByQuery('ETL_REVENUE_BADGE'),
        // }).create(),
        // 'mopa.funnels.filter.attribution_network_name': DataLoaderFactories[
        //     'common.filter.datasource_column'
        // ]({
        //     createdInstanceId: 'mopa.funnels.filter.attribution_network_name',
        //     gcpProjectId: this.defaultGcpProjectId,
        //     queryCacheService: this.queryCache,
        //     datasourceColumnId: 'ATTRIBUTION_NETWORK_NAME',
        //     datasourceSqlMap: BIVizDashboardSales.sqlMap,
        // }).create(),
        // 'mopa.funnels.filter.attribution_campaign_name': DataLoaderFactories[
        //     'common.filter.datasource_column'
        // ]({
        //     createdInstanceId: 'mopa.funnels.filter.attribution_campaign_name',
        //     gcpProjectId: this.defaultGcpProjectId,
        //     queryCacheService: this.queryCache,
        //     datasourceColumnId: 'ATTRIBUTION_CAMPAIGN_NAME',
        //     datasourceSqlMap: BIVizDashboardSales.sqlMap,
        //     filterContext: true,
        // }).create(),
    };

    // FILTERS
    filterDashboardItemLinks: Partial<
        Record<DatasourceColumnId, IDashboardItemDataLoaderLink>
    > = {
        ACTIVITY_DATE: {
            id: 'mopa.funnels.filter.activity_date',
            dashboardItemFactory: DashboardItemFactories['filter.daterange']({
                createdInstanceId: 'mopa.funnels.filter.activity_date',
                datasourceColumnId: 'ACTIVITY_DATE',
                datasourceColumnLabel: 'FUNNEL START DATE',
            }),
            // empty emitter DataRangePicker doesn't need data
            dataLoaderEmitter: new EventEmitter<IDataLoaderResult>(),
        },
        PROJECT_ID: {
            id: 'mopa.funnels.filter.project_id',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'mopa.funnels.filter.project_id',
                datasourceColumnId: 'PROJECT_ID',
                splitable: false,
                selectMode: 'single',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.funnels.filter.project_id']
                    .emitter,
        },
        FUNNELS_MASTER_FUNNEL_STEP_ID: {
            id: 'mopa.funnels.filter.master_funnel_step_id',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'mopa.funnels.filter.master_funnel_step_id',
                datasourceColumnId: 'FUNNELS_MASTER_FUNNEL_STEP_ID',
                splitable: false,
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.funnels.filter.master_funnel_step_id'
                ].emitter,
        },
        FUNNELS_DETAILS_EVENT_NAME: {
            id: 'mopa.funnels.filter.funnel_event',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'mopa.funnels.filter.funnel_event',
                datasourceColumnId: 'FUNNELS_DETAILS_EVENT_NAME',
                splitable: false,
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.funnels.filter.details_event_name']
                    .emitter,
        },
        FUNNELS_DETAILS_SCREEN_NAME: {
            id: 'mopa.funnels.filter.screen_name',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'mopa.funnels.filter.screen_name',
                datasourceColumnId: 'FUNNELS_DETAILS_SCREEN_NAME',
                splitable: false,
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.funnels.filter.details_screen_name'
                ].emitter,
        },
        // SALES_INAPP_SOURCE: {
        //     id: 'mopa.funnels.filter.inapp_source',
        //     dashboardItemFactory: DashboardItemFactories['filter.category']({
        //         createdInstanceId: 'mopa.funnels.filter.inapp_source',
        //         datasourceColumnId: 'SALES_INAPP_SOURCE',
        //     }),
        //     dataLoaderEmitter:
        //         this.filterDataLoaders['mopa.funnels.filter.inapp_source']
        //             .emitter,
        // },
        // SALES_INAPP_PRODUCT_ID: {
        //     id: 'mopa.funnels.filter.inapp_product_id',
        //     dashboardItemFactory: DashboardItemFactories['filter.category']({
        //         createdInstanceId: 'mopa.funnels.filter.inapp_product_id',
        //         datasourceColumnId: 'SALES_INAPP_PRODUCT_ID',
        //     }),
        //     dataLoaderEmitter:
        //         this.filterDataLoaders['mopa.funnels.filter.inapp_product_id']
        //             .emitter,
        // },
        /**
         * ACTIVITY_APP_VERSION: {
         *     id: 'mopa.funnels.filter.app_version',
         *     dashboardItemFactory: DashboardItemFactories['filter.category']({
         *         createdInstanceId: 'mopa.funnels.filter.app_version',
         *         datasourceColumnId: 'ACTIVITY_APP_VERSION',
         *     }),
         *     dataLoaderEmitter:
         *         this.filterDataLoaders[
         *             'mopa.funnels.filter.activity_app_version'
         *         ].emitter,
         * },
         * SEGMENT: {
         *     id: 'mopa.funnels.filter.segments',
         *     dashboardItemFactory: DashboardItemFactories['filter.segments']({
         *         createdInstanceId: 'mopa.funnels.filter.segments',
         *         upliftReferenceSegmentSettingId:
         *             QueryContextSettingID.ABTESTS_UPLIFT_REFERENCE,
         *     }),
         *     dataLoaderEmitter:
         *         this.filterDataLoaders['mopa.funnels.filter.segment'].emitter,
         * },
         * FIREBASE_FIRST_OPEN_DATE: {
         *     id: 'mopa.funnels.filter.firebase_first_open_date',
         *     dashboardItemFactory: DashboardItemFactories['filter.daterange']({
         *         createdInstanceId:
         *             'mopa.funnels.filter.firebase_first_open_date',
         *         datasourceColumnId: 'FIREBASE_FIRST_OPEN_DATE',
         *         splitable: true,
         *     }),
         *     // empty emitter DataRangePicker doesn't need data
         *     dataLoaderEmitter: new EventEmitter<IDataLoaderResult>(),
         * },
         * FIREBASE_APP_STORE: {
         *     id: 'mopa.funnels.filter.firebase_app_store',
         *     dashboardItemFactory: DashboardItemFactories['filter.category']({
         *       createdInstanceId: 'mopa.funnels.filter.firebase_app_store',
         *         datasourceColumnId: 'FIREBASE_APP_STORE',
         *     }),
         *     dataLoaderEmitter:
         *         this.filterDataLoaders['mopa.funnels.filter.firebase_app_store']
         *             .emitter,
         * },
         * FIREBASE_COUNTRY_EUROPEAN_UNION: {
         *     id: 'mopa.funnels.filter.firebase_country_european_union',
         *     dashboardItemFactory: DashboardItemFactories['filter.category']({
         *         createdInstanceId:
         *             'mopa.funnels.filter.firebase_country_european_union',
         *         datasourceColumnId: 'FIREBASE_COUNTRY_EUROPEAN_UNION',
         *     }),
         *     dataLoaderEmitter:
         *         this.filterDataLoaders[
         *             'mopa.funnels.filter.firebase_country_european_union'
         *         ].emitter,
         * },
         * FIREBASE_COUNTRY_TIER: {
         *     id: 'mopa.funnels.filter.firebase_country_tier',
         *     dashboardItemFactory: DashboardItemFactories['filter.category']({
         *         createdInstanceId: 'mopa.funnels.filter.firebase_country_tier',
         *         datasourceColumnId: 'FIREBASE_COUNTRY_TIER',
         *     }),
         *     dataLoaderEmitter:
         *         this.filterDataLoaders[
         *             'mopa.funnels.filter.firebase_country_tier'
         *         ].emitter,
         * },
         * FIREBASE_COUNTRY: {
         *     id: 'mopa.funnels.filter.firebase_country',
         *     dashboardItemFactory: DashboardItemFactories['filter.category']({
         *         createdInstanceId: 'mopa.funnels.filter.firebase_country',
         *         datasourceColumnId: 'FIREBASE_COUNTRY',
         *     }),
         *     dataLoaderEmitter:
         *         this.filterDataLoaders['mopa.funnels.filter.firebase_country']
         *             .emitter,
         * },
         * ATTRIBUTION_TRAFFIC_SOURCE: {
         *     id: 'mopa.funnels.filter.attribution_traffic_source',
         *     dashboardItemFactory: DashboardItemFactories['filter.category']({
         *         createdInstanceId:
         *             'mopa.funnels.filter.attribution_traffic_source',
         *         datasourceColumnId: 'ATTRIBUTION_TRAFFIC_SOURCE',
         *     }),
         *     dataLoaderEmitter:
         *         this.filterDataLoaders[
         *             'mopa.funnels.filter.attribution_traffic_source'
         *         ].emitter,
         * },
         * FIREBASE_INSTALL_VERSION: {
         *     id: 'mopa.funnels.filter.firebase_install_version',
         *     dashboardItemFactory: DashboardItemFactories['filter.category']({
         *         createdInstanceId:
         *             'mopa.funnels.filter.firebase_install_version',
         *         datasourceColumnId: 'FIREBASE_INSTALL_VERSION',
         *     }),
         *     dataLoaderEmitter:
         *         this.filterDataLoaders[
         *             'mopa.funnels.filter.firebase_install_version'
         *         ].emitter,
         * },
         * ETL_PAYER: {
         *     id: 'mopa.funnels.filter.etl_payer',
         *     dashboardItemFactory: DashboardItemFactories['filter.category']({
         *         createdInstanceId: 'mopa.funnels.filter.etl_payer',
         *         datasourceColumnId: 'ETL_PAYER',
         *     }),
         *     dataLoaderEmitter:
         *         this.filterDataLoaders['mopa.funnels.filter.etl_payer'].emitter,
         * },
         * ETL_REVENUE_BADGE: {
         *     id: 'mopa.funnels.filter.etl_revenue_badge',
         *     dashboardItemFactory: DashboardItemFactories['filter.category']({
         *         createdInstanceId: 'mopa.funnels.filter.etl_revenue_badge',
         *         datasourceColumnId: 'ETL_REVENUE_BADGE',
         *     }),
         *     dataLoaderEmitter:
         *         this.filterDataLoaders['mopa.funnels.filter.etl_revenue_badge']
         *             .emitter,
         * },
         * ATTRIBUTION_NETWORK_NAME: {
         *     id: 'mopa.funnels.filter.attribution_network_name',
         *     dashboardItemFactory: DashboardItemFactories['filter.category']({
         *         createdInstanceId:
         *             'mopa.funnels.filter.attribution_network_name',
         *         datasourceColumnId: 'ATTRIBUTION_NETWORK_NAME',
         *     }),
         *     dataLoaderEmitter:
         *         this.filterDataLoaders[
         *             'mopa.funnels.filter.attribution_network_name'
         *         ].emitter,
         * },
         * ATTRIBUTION_CAMPAIGN_NAME: {
         *     id: 'mopa.funnels.filter.attribution_campaign_name',
         *     dashboardItemFactory: DashboardItemFactories['filter.category']({
         *         createdInstanceId:
         *             'mopa.funnels.filter.attribution_campaign_name',
         *         datasourceColumnId: 'ATTRIBUTION_CAMPAIGN_NAME',
         *     }),
         *     dataLoaderEmitter:
         *         this.filterDataLoaders[
         *             'mopa.funnels.filter.attribution_campaign_name'
         *         ].emitter,
         * },
         */
    };

    /////////////////////////////////////////////////////////////////
    ////
    ////    FILTRY
    ////
    ////-------------------------------------------------------------

    override async ngOnInit() {
        super.ngOnInit();
        console.log({
            msg: 'MopaFunnelsComponent.ngOnInit',
            cancellationToken: this.cancellationToken,
        });
    }

    async ngAfterViewInit() {
        console.log({
            msg: 'MopaFunnelsComponent.ngAfterViewInit',
            cancellationToken: this.cancellationToken,
        });

        this.standardDashboardInitialize(this.dashboardInitOptions);
    }

    override getInitialContext(): IQueryContext {
        const initialContext = QueryContextService.INITIAL_CONTEXT;
        const projectId: IMultipleSelectDashboardItem['datasourceColumn'] = {
            id: 'PROJECT_ID',
            state: {
                value: {
                    filterType: 'include',
                    options: ['Enigmatis2'],
                },
                isFilterApplied: true,
                isSplitApplied: false,
            },
        };

        // set initialContext for AppStore to Google Play
        initialContext.datasourceColumns = [projectId];
        // add settings
        initialContext.settings[
            QueryContextSettingID.FUNNELS_LIMIT_DETAIL_STEPS
        ] =
            QueryContextDefaultSettings[
                QueryContextSettingID.FUNNELS_LIMIT_DETAIL_STEPS
            ];
        initialContext.settings[
            QueryContextSettingID.FUNNELS_STEP_COUNT_STRATEGY
        ] =
            QueryContextDefaultSettings[
                QueryContextSettingID.FUNNELS_STEP_COUNT_STRATEGY
            ];
        initialContext.settings[
            QueryContextSettingID.FUNNELS_OTHER_GROUPING_STRATEGY
        ] =
            QueryContextDefaultSettings[
                QueryContextSettingID.FUNNELS_OTHER_GROUPING_STRATEGY
            ];
        // split group default value
        // initialContext.settings[
        //     QueryContextSettingID.ABTESTS_UPLIFT_REFERENCE
        // ] = undefined;

        return initialContext;
    }

    // static cohortOverviewTableColumnsGetter(
    //     context: IQueryContext
    // ): TableColumnItem[] {
    //     return [
    //         {
    //             id: 'cohort_size',
    //             name: 'Cohort size',
    //             nzLeft: false,
    //             // nzAlign: 'center',
    //             nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
    //                 'cohort_size',
    //                 'number'
    //             ),
    //             // nzWidth: '10%',
    //         },
    //         {
    //             id: 'revenue_net_pln',
    //             name: 'Revenue net pln',
    //             nzLeft: false,
    //             // nzAlign: 'center',
    //             nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
    //                 'revenue_net_pln',
    //                 'number'
    //             ),
    //             labelFormatter: (value: any) => {
    //                 return EChartsTooltipHelper.tooltipCurrencyValueFormatter(
    //                     value,
    //                     context!.settings.currency || 'pln'
    //                 );
    //             },
    //             // nzWidth: '90%',
    //         },
    //     ];
    // }

    /**
     * Select first view from dashboardViews
     *
     **/
    // static getDefaultABTestDashboardView(dashboardViews: DashboardView[]) {
    //     return dashboardViews[0];
    // }
}
